import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { McpEditor } from "@/components/mcp/mcp-editor";
import { mcpApi, type McpCatalogueItem, type McpTestResult } from "@/api/mcp";
import { companiesApi } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import {
  Puzzle, Plus, Trash2, Globe, Terminal, Wrench,
  CheckCircle2, XCircle, Loader2, ChevronDown, ChevronRight,
  Pencil, Radio, Shield, RefreshCw, Lock, Building2,
} from "lucide-react";

type TransportType = "http" | "sse" | "stdio";
type AuthType = "none" | "api_key" | "bearer" | "oauth_client_credentials" | "oauth_signin";

const transportIcons: Record<TransportType, typeof Globe> = {
  http: Globe, sse: Radio, stdio: Terminal,
};
const authLabels: Record<AuthType, string> = {
  none: "No Authentication",
  api_key: "API Key",
  bearer: "Bearer Token",
  oauth_client_credentials: "OAuth 2.0 Client Credentials",
  oauth_signin: "OAuth 2.0 (Sign in)",
};

export function McpCatalogue() {
  const queryClient = useQueryClient();

  // Filter: undefined = all, "platform" = platform-wide only, <uuid> = a tenant's view
  const [filter, setFilter] = useState<string | undefined>(undefined);

  const { data: items = [], isLoading } = useQuery({
    queryKey: queryKeys.mcpCatalogue.filtered(filter),
    queryFn: () => mcpApi.listCatalogue(filter ? { companyId: filter } : undefined),
  });

  // Tenants list feeds both the filter dropdown and the editor's "assign to
  // tenant" picker. Platform admins always have access to this endpoint.
  const { data: companies = [] } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: companiesApi.list,
  });
  const companyNameById = useMemo(
    () => new Map(companies.map((c) => [c.id, c.name])),
    [companies],
  );
  const tenantOptions = useMemo(
    () => companies.map((c) => ({ id: c.id, name: c.name })),
    [companies],
  );

  const [mode, setMode] = useState<"list" | "create" | "edit">("list");
  const [editingItem, setEditingItem] = useState<McpCatalogueItem | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, McpTestResult>>({});

  const invalidateAll = () => {
    // Invalidate every scoped list so subsequent tenant page loads see fresh
    // data too. Safer than being surgical given low cache pressure here.
    queryClient.invalidateQueries({ queryKey: ["mcp-catalogue"] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => mcpApi.deleteCatalogue(id),
    onSuccess: invalidateAll,
  });

  const testMutation = useMutation({
    mutationFn: async (item: McpCatalogueItem) => {
      const http = item.httpConfig;
      const result = await mcpApi.test({
        mcpId: item.id,
        transportType: item.transportType,
        url: http?.url,
        headers: http?.headers,
        auth: http?.auth,
      });
      if (result.success && result.tools && result.tools.length > 0) {
        await mcpApi.refreshSchema(item.id, result.tools);
        invalidateAll();
      }
      return { id: item.id, result };
    },
    onSuccess: ({ id, result }) => {
      setTestResults((prev) => ({ ...prev, [id]: result }));
    },
  });

  const toolCount = (item: McpCatalogueItem) =>
    item.toolSchemaCache ? Object.keys(item.toolSchemaCache).length : 0;
  const toolList = (item: McpCatalogueItem) =>
    item.toolSchemaCache ? Object.values(item.toolSchemaCache) : [];

  if (mode !== "list") {
    return (
      <CenterPanel title={mode === "create" ? "Add MCP Server" : "Edit MCP Server"}>
        <McpEditor
          scope={{ kind: "platform" }}
          initialValue={editingItem ?? undefined}
          tenantOptions={tenantOptions}
          onSaved={() => {
            invalidateAll();
            setMode("list");
            setEditingItem(null);
          }}
          onCancel={() => {
            setMode("list");
            setEditingItem(null);
          }}
        />
      </CenterPanel>
    );
  }

  return (
    <CenterPanel title="MCP Catalogue">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-text-secondary">
            {items.length} MCP server{items.length !== 1 ? "s" : ""}
          </p>
          <select
            value={filter ?? ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs"
            title="Filter by scope"
          >
            <option value="">All tenants + platform</option>
            <option value="platform">Platform only</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={() => { setEditingItem(null); setMode("create"); }}>
          <Plus size={16} className="mr-1.5" />
          Add MCP Server
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading catalogue...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Puzzle size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary mb-1">No MCP servers match this filter.</p>
            <Button size="sm" onClick={() => { setEditingItem(null); setMode("create"); }}>
              <Plus size={14} className="mr-1.5" />
              Add MCP
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => {
            const Icon = transportIcons[item.transportType] || Globe;
            const isExpanded = expandedId === item.id;
            const tools = toolList(item);
            const testResult = testResults[item.id];
            const ownerName = item.companyId ? companyNameById.get(item.companyId) ?? "Unknown tenant" : null;

            return (
              <Card key={item.id}>
                <CardContent className="py-0">
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex w-full cursor-pointer flex-wrap items-center justify-between gap-y-2 py-4 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedId(isExpanded ? null : item.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                        <Icon size={20} className="text-accent" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text-primary">{item.displayName || item.name}</p>
                        <p className="truncate text-xs text-text-muted">
                          {item.transportType.toUpperCase()}
                          {item.transportType !== "stdio" && item.httpConfig?.url && (
                            <span> · {item.httpConfig.url}</span>
                          )}
                          {item.transportType === "stdio" && item.stdioConfig?.command && (
                            <span> · {item.stdioConfig.command} {item.stdioConfig.args?.join(" ") || ""}</span>
                          )}
                          {item.description && <span> · {item.description}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={ownerName ? "warning" : "secondary"}>
                        <Building2 size={12} className="mr-1" />
                        {ownerName ?? "Platform"}
                      </Badge>
                      {(item.transportType === "http" || item.transportType === "sse") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); testMutation.mutate(item); }}
                          disabled={testMutation.isPending}
                          title="Re-probe this server for its current tool list"
                        >
                          {testMutation.isPending && testMutation.variables?.id === item.id ? (
                            <Loader2 size={12} className="mr-1 animate-spin" />
                          ) : (
                            <RefreshCw size={12} className="mr-1" />
                          )}
                          Discover tools
                        </Button>
                      )}
                      {toolCount(item) > 0 && (
                        <Badge variant="secondary">
                          <Wrench size={12} className="mr-1" />
                          {toolCount(item)} tools
                        </Badge>
                      )}
                      {item.httpConfig?.auth?.type && item.httpConfig.auth.type !== "none" && (
                        <Badge variant="secondary">
                          <Shield size={12} className="mr-1" />
                          {item.httpConfig.auth.type === "api_key" ? "API Key" :
                           item.httpConfig.auth.type === "bearer" ? "Bearer" :
                           item.httpConfig.auth.type === "oauth_client_credentials" ? "OAuth" : "Auth"}
                        </Badge>
                      )}
                      <Badge variant={item.secretScope === "tenant" ? "warning" : "secondary"}>
                        {item.secretScope === "tenant" ? <Building2 size={12} className="mr-1" /> : <Lock size={12} className="mr-1" />}
                        {item.secretScope === "tenant" ? "Tenant Vault" : "Global Vault"}
                      </Badge>
                      <Badge variant={item.status === "active" ? "success" : "default"}>
                        {item.status}
                      </Badge>
                      {isExpanded ? <ChevronDown size={16} className="text-text-muted" /> : <ChevronRight size={16} className="text-text-muted" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border pb-4 pt-3 space-y-3">
                      <div className="flex items-center gap-2">
                        {(item.transportType === "http" || item.transportType === "sse") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); testMutation.mutate(item); }}
                            disabled={testMutation.isPending}
                          >
                            {testMutation.isPending ? (
                              <Loader2 size={14} className="mr-1.5 animate-spin" />
                            ) : (
                              <RefreshCw size={14} className="mr-1.5" />
                            )}
                            Test & Discover Tools
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => { setEditingItem(item); setMode("edit"); }}>
                          <Pencil size={14} className="mr-1.5" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(item.id); }}
                        >
                          <Trash2 size={14} className="mr-1.5" />
                          Delete
                        </Button>
                      </div>

                      {testResult && (
                        <div className={`flex items-center gap-2 rounded-lg p-2 text-xs ${
                          testResult.success ? "bg-green-500/5 text-green-600" : "bg-red-500/5 text-red-500"
                        }`}>
                          {testResult.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                          {testResult.success
                            ? `Connected - ${testResult.tools?.length || 0} tools discovered and cached`
                            : testResult.error}
                        </div>
                      )}

                      <div className="rounded-lg bg-surface-hover p-3 space-y-1.5">
                        <p className="text-xs font-medium text-text-secondary">Configuration</p>
                        <p className="text-xs text-text-muted">
                          <span className="font-medium">Scope:</span>{" "}
                          {ownerName ? <>Tenant-owned ({ownerName})</> : "Platform-wide"}
                        </p>
                        {item.transportType !== "stdio" && item.httpConfig && (
                          <>
                            <p className="text-xs text-text-muted">
                              <span className="font-medium">URL:</span> {item.httpConfig.url}
                            </p>
                            {item.httpConfig.auth?.type && item.httpConfig.auth.type !== "none" && (
                              <p className="text-xs text-text-muted">
                                <span className="font-medium">Auth:</span> {authLabels[item.httpConfig.auth.type]}
                                {item.httpConfig.auth.type === "api_key" && item.httpConfig.auth.apiKeyHeader && (
                                  <span> (header: {item.httpConfig.auth.apiKeyHeader})</span>
                                )}
                              </p>
                            )}
                            {item.httpConfig.headers && Object.keys(item.httpConfig.headers).length > 0 && (
                              <p className="text-xs text-text-muted">
                                <span className="font-medium">Custom Headers:</span> {Object.keys(item.httpConfig.headers).join(", ")}
                              </p>
                            )}
                          </>
                        )}
                        {item.transportType === "stdio" && item.stdioConfig && (
                          <>
                            <p className="text-xs text-text-muted">
                              <span className="font-medium">Command:</span>{" "}
                              <code className="rounded bg-background px-1 py-0.5 font-mono">
                                {item.stdioConfig.command} {item.stdioConfig.args?.join(" ") || ""}
                              </code>
                            </p>
                            {item.stdioConfig.env && Object.keys(item.stdioConfig.env).length > 0 && (
                              <p className="text-xs text-text-muted">
                                <span className="font-medium">Env:</span> {Object.keys(item.stdioConfig.env).join(", ")}
                              </p>
                            )}
                          </>
                        )}
                        <p className="text-xs text-text-muted">
                          <span className="font-medium">Secret Storage:</span>{" "}
                          {item.secretScope === "tenant" ? "Per-tenant Key Vault (isolated)" : "Platform Key Vault (global)"}
                          {item.vaultSecretRef && (
                            <span className="ml-1.5 text-green-600">(secured)</span>
                          )}
                        </p>
                        {item.toolSchemaUpdatedAt && (
                          <p className="text-xs text-text-muted">
                            <span className="font-medium">Tools last refreshed:</span>{" "}
                            {new Date(item.toolSchemaUpdatedAt).toLocaleString()}
                          </p>
                        )}
                      </div>

                      {tools.length > 0 && (
                        <div>
                          <p className="mb-2 text-xs font-medium text-text-secondary">
                            Available Tools ({tools.length})
                          </p>
                          <div className="grid grid-cols-1 gap-1">
                            {tools.map((tool) => (
                              <div
                                key={tool.name}
                                className="flex items-start gap-2 rounded border border-border px-3 py-2"
                              >
                                <Wrench size={12} className="mt-0.5 shrink-0 text-accent" />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium font-mono text-text-primary">{tool.name}</p>
                                  {tool.description && (
                                    <p className="text-[11px] text-text-muted truncate">{tool.description}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </CenterPanel>
  );
}
